import { Box, styled, Typography } from '@mui/material';
import { ChangeEvent } from 'react';
import { FieldError } from 'react-hook-form';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { EColors, getColor } from 'theme';

const StyledPhoneInput = styled(PhoneInput, {
    shouldForwardProp: prop => prop !== 'hasError',
})<{ hasError: boolean }>(({ hasError }) => ({
    '&.react-tel-input > .form-control': {
        width: '100%',
        height: '48px',
        fontSize: '.875rem',
        borderRadius: '12px',
        border: `1px solid ${hasError ? getColor(EColors.red) : getColor(EColors.v3Border)}`,
        backgroundColor: getColor(EColors.v3Background),
    },

    '&.react-tel-input > .form-control:hover': {
        borderColor: getColor(EColors.v3Dark),
        outline: 'none',
    },
    '&.react-tel-input > .form-control:focus': {
        borderColor: getColor(EColors.v3Dark),
        outline: 'none',
        boxShadow: 'none',
    },
    '&.react-tel-input > .special-label': {
        padding: '0 5px',
        left: '11px',
        fontSize: '.875rem',
        lineHeight: '1em',
        color: getColor(EColors.pureBlack, 0.6),
    },
    // Safari-specific fixes for flag display and alignment
    '&.react-tel-input .selected-flag': {
        display: 'flex !important',
        alignItems: 'center !important',
        justifyContent: 'flex-start',
        height: '100%',
        padding: '0 8px 0 12px',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        WebkitBoxAlign: 'center',
        WebkitFlexAlign: 'center',
        lineHeight: '1.5',
        fontSize: '.875rem',
    },
    '&.react-tel-input .selected-flag .flag': {
        position: 'relative',
        marginTop: '0 !important',
        marginBottom: '0 !important',
        marginRight: '6px',
        WebkitTransform: 'translateY(0) translateZ(0)',
        transform: 'translateY(0) translateZ(0)',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        verticalAlign: 'middle',
        display: 'inline-block',
        alignSelf: 'center',
        top: '0',
    },
    '&.react-tel-input .selected-flag > *:not(.flag):not(.arrow)': {
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        lineHeight: '1.5',
        fontSize: '.875rem',
    },
    '&.react-tel-input .selected-flag .arrow': {
        alignSelf: 'center',
        marginTop: '0 !important',
        marginLeft: '4px',
        verticalAlign: 'middle',
    },
}));

const getCountry = () => {
    try {
        const Locale = Intl.Locale;
        return Locale ? new Locale(navigator.language).region?.toLowerCase() : undefined;
    } catch {
        return undefined;
    }
};

type PhoneFieldProps = {
    label: string;
    error?: FieldError;
    onChange: (
        value: string,
        data: object | CountryData,
        event: ChangeEvent<HTMLInputElement>,
        formattedValue: string
    ) => void;
    onBlur?: () => void;
    value?: string;
    disabled?: boolean;
    required?: boolean;
};

export const PhoneField = ({ label, error, value, required = false, onChange, onBlur }: PhoneFieldProps) => {
    return (
        <Box>
            <StyledPhoneInput
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                country={getCountry()}
                specialLabel={required ? label + ' *' : label}
                hasError={!!error}
                placeholder="Enter phone number"
            />
            {!!error && (
                <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                    {error.message}
                </Typography>
            )}
        </Box>
    );
};
